/**
 * Shared formatting utilities — import from here instead of defining locally per page.
 */

export const fmtNum = (n) => new Intl.NumberFormat('pt-PT').format(n);

export const fmtDate = (d) => {
  if (!d) return '—';
  if (typeof d === 'string' && d.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(d)) {
    const parts = d.substring(0, 10).split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/** Returns a Date at 00:00:00.000 UTC for the given date's calendar day */
export const toUtcStartOfDay = (d) => {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
};

/** Returns a Date at 23:59:59.999 UTC for the given date's calendar day */
export const toUtcEndOfDay = (d) => {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
};

/** Formats an ISO datetime string to "dd/mm/yyyy · hh:mm" */
export const fmtDateTime = (iso, useUtc = false) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const day = String(useUtc ? d.getUTCDate() : d.getDate()).padStart(2, '0');
  const month = String(useUtc ? d.getUTCMonth() + 1 : d.getMonth() + 1).padStart(2, '0');
  const year = useUtc ? d.getUTCFullYear() : d.getFullYear();
  const h = String(useUtc ? d.getUTCHours() : d.getHours()).padStart(2, '0');
  const m = String(useUtc ? d.getUTCMinutes() : d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} · ${h}:${m}`;
};

/** Formats seconds (or minutes) to "27s", "2m 5s", "1h 15m". */
export const fmtDwell = (value, isMinutes = false) => {
  if (!value || value <= 0) return '0s';
  const totalSeconds = isMinutes ? Math.round(value * 60) : Math.round(value);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const h = Math.floor(m / 60);
  const remM = m % 60;

  if (h > 0) return `${h}h ${String(remM).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
};

const AVATAR_COLORS = ['#1B75BB', '#2E7D32', '#C62828', '#6A1B9A', '#EF6C00'];

/** Derives a deterministic avatar colour from a client's name. */
export const clientAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

/** Generates 1-2 letter initials from a client name. */
export const clientInitials = (name) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Standard TableHead Row SX adhering to the design system in /Gateway/Users */
export const TABLE_HEAD_ROW_SX = {
  bgcolor: '#f9fafb',
  '& .MuiTableCell-root, & .MuiTableCell-head': {
    fontSize: '0.725rem',
    fontWeight: 700,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    py: 1.25,
    whiteSpace: 'nowrap',
    background: '#f9fafb',
  },
};

/** Shared sx for uppercase table header cells. */
export const TH_SX = {
  fontSize: '0.725rem',
  fontWeight: 700,
  color: '#9ca3af',
  letterSpacing: '0.06em',
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  py: 1.25,
  bgcolor: '#f9fafb',
};

/** Helper to serialize flat/array objects into URL query strings. */
export const buildQueryString = (params) => {
  if (!params) return '';
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val === undefined || val === null || val === '') return;
    if (Array.isArray(val)) {
      val.forEach(v => {
        if (v) searchParams.append(key, v);
      });
    } else if (val instanceof Date) {
      searchParams.append(key, val.toISOString());
    } else {
      searchParams.append(key, val);
    }
  });
  return searchParams.toString();
};
